import * as Errors from "./Errors";
import api from "./Api";
import Subpopulation from "./Subpopulation";

const IntSerializer = {
  serialize(i) {
    if (i !== null) {
      return i.toString();
    } else {
      return "";
    }
  },

  deserialize(data) {
    return parseInt(data);
  }
};

const IntArraySerializer = {
  serialize(arr) {
    return arr.join(",");
  },

  deserialize(data) {
    return data.split(",").map(d => parseInt(d));
  }
};

const SubpopulationIdSerializer = {
  serialize(subpops) {
    return subpops.map(s => `${s.variableId}!${s.categoryIds.join(".")}`).join(",")
  },

  deserialize(data) {
    return data.split(",").map(s => {
      let parts = s.split("!");
      return {
        variableId: parseInt(parts[0]),
        categoryIds: parts[1].split(".")
      }
    });
  }
};

const BooleanSerializer = {
  serialize(val) {
    return val ? "t" : "f";
  },

  deserialize(data) {
    return data === "t";
  }
};

const StringSerializer = {
  serialize(val) {
    return val;
  },

  deserialize(data) {
    return data;
  }
};

function transformApiMetadataValues(serializer, metadataValues) {
  serializer.variables = metadataValues.variables;
  serializer.samples = metadataValues.samples;
  serializer.place = metadataValues.place;
  serializer.subpopulations = [];

  for(const subpopVar of metadataValues.subpopulationVariables) {
    let subp = new Subpopulation(subpopVar.variable, subpopVar.categories);
    serializer.subpopulations.push(subp);
    for (const cat of metadataValues.selectedSubpopulationCategoryIds) {
      // If the category doesn't belong to the variable, it gets ignored.
      subp.addCategory(cat);
    }
  }
}

const Version1Transformer = {
  asString: function(serializer) {
    const subpopData = serializer.subpopulations.map(s => ({ variableId: s.variable.id, categoryIds: s.selectedCategories.map(c => c.id)}));
    const values = [
      IntArraySerializer.serialize(serializer.variables.map(v => v.id)),
      IntArraySerializer.serialize(serializer.samples.map(s => s.id)),
      SubpopulationIdSerializer.serialize(subpopData),
      IntSerializer.serialize(serializer.place?.id || null),
      BooleanSerializer.serialize(serializer.showGraphs),
      StringSerializer.serialize(serializer.measure)
    ];

    return values.join("~");
  },

  fromString: async function(serializer, payload) {
    const values = payload.split("~");

    if (values.length !== 6) {
      throw new Errors.SessionSerializationError("Invalid session data: incorrect number of fields");
    }

    const metadataJson = {
      variableIds: IntArraySerializer.deserialize(values[0]),
      sampleIds: IntArraySerializer.deserialize(values[1]),
      subpopulationIds: SubpopulationIdSerializer.deserialize(values[2]),
      placeId: IntSerializer.deserialize(values[3])
    };

    const showGraphs = BooleanSerializer.deserialize(values[4]);
    const measure = StringSerializer.deserialize(values[5]);

    const metadataValues = await api.restoreSession(1, metadataJson);

    serializer.version = 1;
    serializer.showGraphs = showGraphs;
    serializer.measure = measure;
    transformApiMetadataValues(serializer, metadataValues);

    return serializer;
  }
};

const Version2Transformer = {
  asString: function(serializer) {
    const subpopData = serializer.subpopulations.map(s => ({ variableMnemonic: s.variable.mnemonic, categoryKeys: s.selectedCategories.map(c => c.key)}));
    const values = {
      variables: serializer.variables.map(v => v.mnemonic),
      samples: serializer.samples.map(s => s.name),
      subpopulations: subpopData,
      place: serializer.place?.key || null,
      showGraphs: serializer.showGraphs,
      measure: serializer.measure
    };

    return window.btoa(JSON.stringify(values));
  },

  fromString: async function(serializer, payload) {
    const values = JSON.parse(window.atob(payload));

    const metadataJson = {
      variableMnemonics: values.variables,
      sampleNames: values.samples,
      subpopulationKeys: values.subpopulations,
      placeKey: values.place || null
    };

    const showGraphs = values.showGraphs;
    const measure = values.measure;

    const metadataValues = await api.restoreSession(2, metadataJson);

    serializer.version = 2;
    serializer.showGraphs = showGraphs;
    serializer.measure = measure;
    transformApiMetadataValues(serializer, metadataValues);

    return serializer;
  }
};

const CURRENT_VERSION = 2;

const VERSIONS = {
  1: Version1Transformer,
  2: Version2Transformer
};

class SessionSerializer {
  constructor() {
    this.version = CURRENT_VERSION;
    this.variables = [];
    this.samples = [];
    this.subpopulations = [];
    this.showGraphs = true;
    this.measure = null;
    this.place = null;
  }

  serialize() {
    const values = [];
    const version = VERSIONS[this.version];

    if (!version) {
      throw new Errors.SessionSerializationError("Unknown version: " + this.version);
    }

    values.push(this.version);
    values.push(version.asString(this));

    return this.constructor.encode(values.join("~"));
  }

  static fromStore(selectionStore) {
    const serializer = new SessionSerializer();
    serializer.variables = selectionStore.selectedVariables;
    serializer.samples = selectionStore.selectedSamples;
    serializer.subpopulations = selectionStore.selectedSubpopulations;
    serializer.place = selectionStore.selectedPlace !== null && selectionStore.selectedPlace.id !== -1 ? selectionStore.selectedPlace : null;
    serializer.showGraphs = selectionStore.dataDisplayOptions.showGraphs;
    serializer.measure = selectionStore.dataDisplayOptions.measure.value;
    return serializer;
  }

  static async fromSerializedData(data) {
    if (!data) {
      throw new Errors.SessionSerializationError("Empty session data");
    }

    const serializer = new SessionSerializer();

    const [_, version, values] = this.decode(data).match(/(^\d+~)(.*$)/s);

    if (version === undefined || values === undefined) {
      throw new Errors.SessionSerializationError("Invalid session data: no fields");
    }

    serializer.version = parseInt(version);
    const transformer = VERSIONS[serializer.version];

    if (!transformer) {
      throw new Errors.SessionSerializationError("Unknown version: " + serializer.version);
    }

    await transformer.fromString(serializer, values);

    return serializer;
  }

  static encode(str) {
    const pass1 = encodeURIComponent(str);

    // encodeURIComponent ignores some characters that should be escaped according to RFC3986
    return pass1.replace(/[!'()*]/g, c => "%" + c.charCodeAt(0).toString(16))
  }

  static decode(str) {
    return decodeURIComponent(str);
  }
}

export default SessionSerializer;